import React, { useState, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useGetDriverPaymentsQuery, useMarkDriverPaymentPaidMutation } from "../../../Api/attoDeskApi";
import { IDriverPayment } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PaidIcon from "@mui/icons-material/Paid";
import PersonIcon from "@mui/icons-material/Person";
import PrintIcon from "@mui/icons-material/Print";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as XLSX from "xlsx";

const BRAND = "#2f3891";

const thSx = {
  fontWeight: 700, fontSize: 11, color: "#64748b",
  textTransform: "uppercase" as const, letterSpacing: "0.06em",
  py: 1.25, px: 1.5, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc",
  whiteSpace: "nowrap" as const,
};
const tdSx = {
  py: 1.5, px: 1.5, borderBottom: "1px solid #f1f5f9",
  fontSize: 12, color: "#1e293b", verticalAlign: "middle" as const,
};

const DriverPaymentList: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | "pending" | "paid">("");
  const [markingId, setMarkingId] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (searchText.trim()) params.search = searchText.trim();
      if (statusFilter)      params.status = statusFilter;
      const response = await axios.get(`${API_URL}/pdf/driver-payments-report`, {
        params, responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `driver-payments-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { /* silent */ } finally { setPdfLoading(false); }
  };

  const { data, isLoading, refetch } = useGetDriverPaymentsQuery({
    searchText: searchText || undefined,
    status: statusFilter || undefined,
  });

  const [markPaid] = useMarkDriverPaymentPaidMutation();

  const payments: IDriverPayment[] = useMemo(() => {
    if (!data || !Array.isArray(data.data)) return [];
    return data.data as IDriverPayment[];
  }, [data]);

  // ── Summary stats ──────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total       = payments.length;
    const pending     = payments.filter((p) => p.status === "pending").length;
    const paid        = payments.filter((p) => p.status === "paid").length;
    const totalAmount = payments.reduce((s, p) => s + (p.total_payment || 0), 0);
    const pendingAmt  = payments.filter((p) => p.status === "pending").reduce((s, p) => s + (p.total_payment || 0), 0);
    return { total, pending, paid, totalAmount, pendingAmt };
  }, [payments]);

  const handleExportToExcel = () => {
    const data = payments.map((p, i) => ({
      "#":            i + 1,
      "Driver":       p.driver_name || "",
      "Invoice":      p.invoice_no || "",
      "Trip Date":    p.trip_date ? dayjs(p.trip_date).format("DD MMM YYYY HH:mm") : "",
      "Hours":        p.hours_worked,
      "Rate ($/h)":   p.hourly_rate,
      "Base Salary":  p.base_salary.toFixed(2),
      "Tips":         p.tips.toFixed(2),
      "Total":        p.total_payment.toFixed(2),
      "Status":       p.status === "paid" ? "Paid" : "Pending",
      "Paid At":      p.paid_at ? dayjs(p.paid_at).format("DD MMM YYYY") : "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map((k) => ({
      wch: Math.max(k.length, ...data.map((r: any) => String(r[k] || "").length)) + 2,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Driver Payments");
    XLSX.writeFile(wb, `driver_payments_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleMarkPaid = async (id: number) => {
    setMarkingId(id);
    await markPaid({ id });
    refetch();
    showMessage("Payment marked as paid.");
    setMarkingId(null);
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#0f172a" }}>Driver Payments</Typography>
        <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>
          Track and manage driver payment records from completed trips
        </Typography>
      </Box>

      {/* Stats cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {[
          { label: "Total Records", value: stats.total, icon: <PersonIcon />, color: "#2f3891", bg: "#eef2ff" },
          { label: "Pending Payment", value: stats.pending, icon: <HourglassEmptyIcon />, color: "#f59e0b", bg: "#fffbeb" },
          { label: "Paid", value: stats.paid, icon: <CheckCircleIcon />, color: "#16a34a", bg: "#f0fdf4" },
          { label: "Total Amount", value: `$${stats.totalAmount.toFixed(2)}`, icon: <AttachMoneyIcon />, color: "#0ea5e9", bg: "#f0f9ff" },
          { label: "Pending Amount", value: `$${stats.pendingAmt.toFixed(2)}`, icon: <PaidIcon />, color: "#ef4444", bg: "#fef2f2" },
        ].map((s, i) => (
          <Card key={i} elevation={0} sx={{ flex: "1 1 140px", border: "1px solid #e2e8f0", borderRadius: 2.5, p: 2, minWidth: 130 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: s.bg, display: "flex", alignItems: "center", justifyContent: "center", color: s.color }}>
                {React.cloneElement(s.icon, { sx: { fontSize: 16 } })}
              </Box>
              <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{s.label}</Typography>
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</Typography>
          </Card>
        ))}
      </Box>

      {/* Filters */}
      <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2.5, p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField size="small" placeholder="Search driver, invoice..."
            value={searchText} onChange={(e) => setSearchText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment> }}
            sx={{ minWidth: 240 }} />
          <Box sx={{ display: "flex", gap: 1 }}>
            {(["", "pending", "paid"] as const).map((s) => (
              <Chip key={s} label={s === "" ? "All" : s === "pending" ? "Pending" : "Paid"}
                size="small" clickable onClick={() => setStatusFilter(s)}
                sx={{
                  fontWeight: 700, fontSize: 11,
                  bgcolor: statusFilter === s ? (s === "paid" ? "#f0fdf4" : s === "pending" ? "#fffbeb" : "#eef2ff") : "#f1f5f9",
                  color: statusFilter === s ? (s === "paid" ? "#16a34a" : s === "pending" ? "#f59e0b" : BRAND) : "#64748b",
                  border: `1px solid ${statusFilter === s ? (s === "paid" ? "#bbf7d0" : s === "pending" ? "#fde68a" : "#c7d2fe") : "#e2e8f0"}`,
                }} />
            ))}
          </Box>
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <Button size="small" variant="outlined"
              startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <PrintIcon sx={{ fontSize: 15 }} />}
              onClick={handleDownloadPdf} disabled={pdfLoading}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", borderColor: "#e2e8f0" }}>
              {pdfLoading ? "Generating…" : "Print"}
            </Button>
            {payments.length > 0 && (
              <Button size="small" variant="outlined"
                startIcon={<TableChartOutlinedIcon sx={{ fontSize: 15 }} />}
                onClick={handleExportToExcel}
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#16a34a", borderColor: "#e2e8f0" }}>
                Excel
              </Button>
            )}
          </Box>
        </Box>
      </Card>

      {/* Table */}
      <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2.5, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["#", "Driver", "Invoice", "Trip Date", "Hours", "Rate", "Base Salary", "Tips", "Total", "Status", "Paid At", "Action"].map((h) => (
                  <TableCell key={h} sx={thSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={12} sx={{ textAlign: "center", py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} sx={{ textAlign: "center", py: 6 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                      <PaidIcon sx={{ fontSize: 40, color: "#cbd5e1" }} />
                      <Typography sx={{ fontSize: 14, color: "#94a3b8", fontWeight: 600 }}>No payment records found</Typography>
                      <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>
                        Complete a trip from the Driver Schedule page to create a payment record.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((p, idx) => (
                  <TableRow key={p.id} sx={{ "&:hover": { bgcolor: "#f8fafc" }, "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ ...tdSx, color: "#94a3b8", width: 36 }}>{idx + 1}</TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{p.driver_name || "—"}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND }}>{p.invoice_no || "—"}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12, color: "#374151" }}>
                        {p.trip_date ? dayjs(p.trip_date).format("DD MMM YYYY") : "—"}
                      </Typography>
                      {p.trip_date && dayjs(p.trip_date).isValid() && (
                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                          {dayjs(p.trip_date).format("h:mm A")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>{p.hours_worked}h</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12, color: "#475569" }}>${p.hourly_rate}/h</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12, color: "#0f172a" }}>${p.base_salary.toFixed(2)}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12, color: "#0f172a" }}>${p.tips.toFixed(2)}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#16a34a" }}>${p.total_payment.toFixed(2)}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Chip label={p.status === "paid" ? "Paid" : "Pending"} size="small"
                        sx={{
                          height: 20, fontSize: 10, fontWeight: 700,
                          color: p.status === "paid" ? "#16a34a" : "#f59e0b",
                          bgcolor: p.status === "paid" ? "#f0fdf4" : "#fffbeb",
                          border: `1px solid ${p.status === "paid" ? "#bbf7d0" : "#fde68a"}`,
                        }} />
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                        {p.paid_at ? dayjs(p.paid_at).format("DD MMM YYYY") : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                      {p.status === "pending" && (
                        <Tooltip title="Mark as paid" arrow>
                          <Button size="small" variant="contained"
                            disabled={markingId === p.id}
                            onClick={() => handleMarkPaid(p.id)}
                            startIcon={markingId === p.id ? <CircularProgress size={12} /> : <CheckCircleIcon />}
                            sx={{
                              fontSize: 11, fontWeight: 700, py: 0.5, px: 1.5, borderRadius: 1.5,
                              textTransform: "none", bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" },
                            }}>
                            Mark Paid
                          </Button>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default DriverPaymentList;
